import type {
  CheckoutAuthorizationExchangeResponse,
  CheckoutSessionAuthorization,
  CheckoutRestartRequest,
  CheckoutMethod,
} from '../api/checkout-session-types'

const AUTHORIZATION_STORAGE_PREFIX = 'checkout-session-authorization:'
const OPERATION_KEY_STORAGE_PREFIX = 'checkout-session-operation:'
const SUCCESSOR_METHOD_STORAGE_PREFIX = 'checkout-successor-method:'

export interface SuccessorMethodIntent {
  provider: string
  pm_id: string
  purchase_option_id: number
  continue_checkout: boolean
}

export function saveSuccessorMethodIntent(sessionNo: string, method: SuccessorMethodIntent): void {
  window.sessionStorage.setItem(
    `${SUCCESSOR_METHOD_STORAGE_PREFIX}${sessionNo}`,
    JSON.stringify(method)
  )
}

export function loadSuccessorMethodIntent(sessionNo: string): SuccessorMethodIntent | null {
  const raw = window.sessionStorage.getItem(`${SUCCESSOR_METHOD_STORAGE_PREFIX}${sessionNo}`)
  if (!raw) return null
  try {
    const value = JSON.parse(raw) as Partial<SuccessorMethodIntent>
    if (
      typeof value.provider !== 'string' ||
      !value.provider.trim() ||
      typeof value.pm_id !== 'string' ||
      !value.pm_id.trim() ||
      !Number.isSafeInteger(value.purchase_option_id) ||
      typeof value.continue_checkout !== 'boolean'
    ) {
      throw new Error('Invalid successor method')
    }
    return value as SuccessorMethodIntent
  } catch {
    throw new Error('CHECKOUT_SUCCESSOR_METHOD_UNAVAILABLE')
  }
}

export function clearSuccessorMethodIntent(sessionNo: string): void {
  window.sessionStorage.removeItem(`${SUCCESSOR_METHOD_STORAGE_PREFIX}${sessionNo}`)
}

export function consumeSuccessorMethodContinuation(
  sessionNo: string,
  selectedMethod: CheckoutMethod
): boolean {
  const intent = loadSuccessorMethodIntent(sessionNo)
  if (!intent || !intent.continue_checkout) return false
  const matches =
    intent.provider === selectedMethod.provider &&
    intent.pm_id === selectedMethod.pm_id &&
    intent.purchase_option_id === selectedMethod.purchase_option_id
  clearSuccessorMethodIntent(sessionNo)
  return matches
}

export interface CheckoutEntry {
  sessionNo: string
  authorizationCode: string | null
  returnedExecutionNo: string | null
  /**
   * 是否通过渠道的独立取消地址返回。
   *
   * 浏览器回跳只表示用户回到了商户站点，不构成支付结果；只有提供独立取消地址的渠道
   * 才能表达"用户主动放弃"。其余渠道恒为 false，由服务端对账决定真值。
   */
  cancellationRequested: boolean
}

function requireCrypto(): Crypto {
  if (typeof crypto === 'undefined' || typeof crypto.getRandomValues !== 'function') {
    throw new Error('Secure browser cryptography is required')
  }
  return crypto
}

function canonicalizeCheckoutOperationInput(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(canonicalizeCheckoutOperationInput)
  }
  if (value === null || typeof value !== 'object') {
    return value
  }
  return Object.fromEntries(
    Object.entries(value)
      .filter(([, entry]) => entry !== undefined)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entry]) => [key, canonicalizeCheckoutOperationInput(entry)])
  )
}

export async function digestCheckoutOperationInput(value: unknown): Promise<string> {
  const browserCrypto = requireCrypto()
  if (!browserCrypto.subtle) {
    throw new Error('Secure browser cryptography is required')
  }
  const input = new TextEncoder().encode(JSON.stringify(canonicalizeCheckoutOperationInput(value)))
  const digest = await browserCrypto.subtle.digest('SHA-256', input)
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
}

export function createCheckoutIdempotencyKey(): string {
  const bytes = new Uint8Array(24)
  requireCrypto().getRandomValues(bytes)
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('')
}

export function parseCheckoutEntry(location: Location = window.location): CheckoutEntry {
  const query = new URLSearchParams(location.search)
  const fragment = new URLSearchParams(location.hash.replace(/^#/, ''))
  const pathMatch = location.pathname.match(/^\/payment-return\/([^/]+)\/([^/]+)$/)
  const sessionNo = decodeURIComponent(
    pathMatch?.[1] ?? query.get('checkout_session_no') ?? ''
  ).trim()
  const returnedExecutionNo = pathMatch?.[2] ? decodeURIComponent(pathMatch[2]).trim() : null
  const result = query.get('result')?.trim().toLowerCase()

  return {
    sessionNo,
    authorizationCode: fragment.get('authorization_code')?.trim() || null,
    returnedExecutionNo,
    cancellationRequested: result === 'cancel',
  }
}

function scrubCheckoutAuthorizationCode(): void {
  const url = new URL(window.location.href)
  if (!url.hash) {
    return
  }
  url.hash = ''
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}`)
}

export function captureCheckoutEntry(location: Location = window.location): CheckoutEntry {
  const entry = parseCheckoutEntry(location)
  if (location === window.location) {
    scrubCheckoutAuthorizationCode()
  }
  return entry
}

export function replaceCheckoutReturnWithSessionLocation(sessionNo: string): void {
  const query = new URLSearchParams({ checkout_session_no: sessionNo })
  window.history.replaceState(window.history.state, '', `/checkout?${query.toString()}`)
}

export function saveCheckoutAuthorization(
  sessionNo: string,
  exchange: CheckoutAuthorizationExchangeResponse
): CheckoutSessionAuthorization {
  const authorization: CheckoutSessionAuthorization = {
    sessionNo,
    sessionToken: exchange.session_token,
    sessionTokenExpiresAt: exchange.session_token_expires_at,
    resultHandoffToken: exchange.result_handoff_token,
    resultHandoffTokenExpiresAt: exchange.result_handoff_expires_at,
    revision: exchange.revision,
  }
  window.sessionStorage.setItem(
    `${AUTHORIZATION_STORAGE_PREFIX}${sessionNo}`,
    JSON.stringify(authorization)
  )
  return authorization
}

export function loadCheckoutAuthorization(sessionNo: string): CheckoutSessionAuthorization | null {
  const raw = window.sessionStorage.getItem(`${AUTHORIZATION_STORAGE_PREFIX}${sessionNo}`)
  if (!raw) {
    return null
  }
  try {
    const value = JSON.parse(raw) as Partial<CheckoutSessionAuthorization>
    if (
      value.sessionNo !== sessionNo ||
      typeof value.sessionToken !== 'string' ||
      typeof value.sessionTokenExpiresAt !== 'string' ||
      typeof value.resultHandoffToken !== 'string' ||
      typeof value.resultHandoffTokenExpiresAt !== 'string' ||
      typeof value.revision !== 'number'
    ) {
      return null
    }
    return value as CheckoutSessionAuthorization
  } catch {
    return null
  }
}

export function checkoutMutationAuthorizationUsable(
  authorization: CheckoutSessionAuthorization,
  now = Date.now()
): boolean {
  return Date.parse(authorization.sessionTokenExpiresAt) > now
}

export function getOrCreateCheckoutOperationKey(sessionNo: string, operation: string): string {
  const storageKey = `${OPERATION_KEY_STORAGE_PREFIX}${sessionNo}:${operation}`
  const existing = window.sessionStorage.getItem(storageKey)?.trim()
  if (existing) {
    return existing
  }
  const key = createCheckoutIdempotencyKey()
  window.sessionStorage.setItem(storageKey, key)
  return key
}

export function clearCheckoutOperationKey(sessionNo: string, operation: string): void {
  window.sessionStorage.removeItem(`${OPERATION_KEY_STORAGE_PREFIX}${sessionNo}:${operation}`)
}

const RESTART_INTENT_STORAGE_PREFIX = 'checkout-session-restart:'

export function saveCheckoutRestartIntent(
  sessionNo: string,
  request: CheckoutRestartRequest
): void {
  window.sessionStorage.setItem(
    `${RESTART_INTENT_STORAGE_PREFIX}${sessionNo}`,
    JSON.stringify(request)
  )
}

export function loadCheckoutRestartIntent(sessionNo: string): CheckoutRestartRequest | null {
  const raw = window.sessionStorage.getItem(`${RESTART_INTENT_STORAGE_PREFIX}${sessionNo}`)
  if (!raw) return null
  try {
    const value: unknown = JSON.parse(raw)
    if (
      typeof value === 'object' &&
      value !== null &&
      'expected_revision' in value &&
      typeof value.expected_revision === 'number' &&
      'confirmed_unpaid' in value &&
      'confirmation_version' in value &&
      Number.isSafeInteger(value.expected_revision) &&
      value.expected_revision > 0 &&
      value.confirmed_unpaid === true &&
      value.confirmation_version === 'paypal_unpaid_v1'
    )
      return {
        expected_revision: value.expected_revision,
        confirmed_unpaid: value.confirmed_unpaid,
        confirmation_version: value.confirmation_version,
      }
  } catch {
    throw new Error('Stored checkout restart intent is invalid')
  }
  throw new Error('Stored checkout restart intent is invalid')
}

export function clearCheckoutRestartIntent(sessionNo: string): void {
  window.sessionStorage.removeItem(`${RESTART_INTENT_STORAGE_PREFIX}${sessionNo}`)
}
